import { useCallback, useMemo, useState } from 'react'
import { useBillers, useGateways, useTransactions, useExportTransactions } from '../../api/queries'
import type { Transaction } from '../../api/types'
import { useAuthenticatedSession } from '../../auth/session'
import { BffError } from '../../bff/errors'
import { useT } from '../../i18n'
import { Button, EmptyState, Pagination, Skeleton, useToast } from '../../ui'
import { DetailPanel } from './DetailPanel'
import { useProfile } from '../../api/queries'
import { FilterBar } from './FilterBar'
import { TransactionsNote } from './TransactionsNote'
import { exposedFields, FIELD_BENEFICIARY, FIELD_GATEWAY, FIELD_SERVICE_TYPE, filterableFields } from './exposure'
import { SummaryStrip } from './SummaryStrip'
import { TransactionsTable } from './TransactionsTable'
import {
  defaultRange, isNarrowed, toQuery, type Filters, withFilterChange,
} from './filters'
import screen from '../screen.module.css'

export interface TransactionsProps {
  readonly filters: Filters
  readonly onFilters: (next: Filters) => void
  /** Injected so "now" is deterministic in tests and identical across the
   *  parse/serialise round trip within one render. */
  readonly now: Date
}

/**
 * The reconciliation workhorse. Composition only — every piece of behaviour it
 * needs lives somewhere testable on its own: filters in `filters.ts`, the
 * request in `useTransactions`, formatting in `businessTime`.
 *
 * The four states from the plan's response contract are all handled here and
 * are deliberately distinguishable:
 *  - loading with nothing yet → skeletons shaped like the table
 *  - loading with previous rows → previous rows dimmed, filter bar live
 *  - empty because there is no data at all vs empty because the filter matched
 *    nothing → different copy and different actions
 *  - error → the table region fails, the filter bar keeps working
 */
export function Transactions({ filters, onFilters, now }: TransactionsProps) {
  const t = useT()
  const { businessTime, user } = useAuthenticatedSession()
  const { notify } = useToast()
  const [selected, setSelected] = useState<Transaction | null>(null)

  // The organisation's settlement cut-off (FR-41) powers the "Settlement day"
  // checkbox; it is data on the organisation, read with the profile, never a
  // constant here.
  const profile = useProfile()
  // The profile is cached under one key, so right after an organisation
  // switch it can still describe the previous organisation: it is read only
  // once it names the selected one.
  const orgProfile = profile.data?.biller_id === user.billerId ? profile.data : undefined
  // FR-42, AB-40: the beneficiary filter exists where the identity is
  // exposed, which is to an internal user acting for a beneficiary
  // organisation, and the API refuses the parameter anywhere else (400). So
  // a hand-edited URL, or one kept across an organisation switch, is
  // stripped of it here rather than turning the list into an error view.
  const allBillersMode = user.isInternal && user.billerId == null
  const beneficiaryMode = user.isInternal && orgProfile?.organisation_kind === 'beneficiary'
  // A settlement day is the beneficiary's: it reconciles what a provider's
  // switch settled to it by the cut-off. A provider has none, whatever its
  // registry row says.
  const settlementCutOff = orgProfile?.organisation_kind === 'beneficiary' ? orgProfile.settlement_day_start : undefined
  const query = useMemo(
    () => toQuery({
      ...filters,
      ...(allBillersMode ? {} : { biller: '' }),
      ...(beneficiaryMode ? {} : { beneficiary: '' }),
    }),
    [filters, allBillersMode, beneficiaryMode],
  )
  const { data, error, isPending, isFetching, refetch } = useTransactions(query)
  const exporter = useExportTransactions()
  // Populates the gateway filter. A biller user is scoped by the server to
  // their assignment; an internal user gets the whole catalogue UNLESS a
  // biller is selected in the sidebar — then the list is narrowed to that
  // biller's gateways, because the transaction rows already are, and a
  // dropdown offering the others could only ever produce an empty table.
  const gateways = useGateways(user.billerId ? { biller: user.billerId } : {})

  // The biller dimension exists only for an internal user browsing every
  // biller at once. A biller user is locked to their own organisation, and an
  // internal user who selected one in the sidebar has already scoped the
  // whole session — in both cases a biller filter and column would restate
  // what the sidebar already says on every row.
  //
  // Deliberately NOT narrowed to providers, unlike the assignment select on
  // the Gateways screen: the API answers `?biller=` with the named
  // organisation's own boundary — a provider's gateways, a beneficiary's
  // slices (biller-api `applyBillerFilter` → `rowScope`) — so choosing a
  // beneficiary here shows exactly the rows its users see, which is the
  // one-off check this control exists for.
  const billers = useBillers(allBillersMode)
  // A transaction row carries its gateway, not its biller: the biller is the
  // holder of the gateway assignment — the same rule the server scopes rows
  // by — and the gateway catalogue above already knows each holder.
  const billerOfGateway = useMemo(() => {
    if (!allBillersMode) return undefined
    const byGateway = new Map((gateways.data ?? []).map((g) => [g.gateway_id, g.biller_name]))
    return (gatewayId: string) => byGateway.get(gatewayId) ?? ''
  }, [allBillersMode, gateways.data])

  const change = useCallback(
    (changes: Partial<Filters>) => onFilters(withFilterChange(filters, changes)),
    [filters, onFilters],
  )

  const sort = useCallback(
    (sortKey: string) => {
      const sortBy = sortKey === 'amount' ? 'amount' : 'date'
      // Clicking the active column flips direction; a different column starts
      // at descending, which is what "most recent" and "largest" both mean.
      const sortDir = filters.sortBy === sortBy && filters.sortDir === 'desc' ? 'asc' : 'desc'
      change({ sortBy, sortDir })
    },
    [filters.sortBy, filters.sortDir, change],
  )

  const runExport = () => {
    exporter.mutate(query, {
      onSuccess: (filename) => notify('success', t('tx.exportDone', { filename })),
      onError: (err) =>
        notify(
          'danger',
          err instanceof BffError && err.requestId
            ? `${err.message} — ${t('error.reference', { requestId: err.requestId })}`
            : err.message,
        ),
    })
  }

  const rows = data?.transactions ?? []
  // FR-38: the page names what it exposes; every surface below renders from it.
  const exposed = useMemo(() => exposedFields(data?.scope), [data])
  const filterable = useMemo(() => filterableFields(data?.scope), [data])
  const narrowed = isNarrowed(filters, businessTime, now)

  return (
    <>
      <div className={screen.header}>
        <div>
          <h1 className={screen.title}>{t('nav.transactions')}</h1>
          <p className={screen.subtitle}>
            {t('shell.timezoneNote', { timezone: businessTime.timezone })}
          </p>
        </div>
        <Button
          variant="secondary"
          onClick={runExport}
          loading={exporter.isPending}
          disabled={rows.length === 0}
        >
          {exporter.isPending ? t('tx.exporting') : t('tx.export')}
        </Button>
      </div>

      <TransactionsNote />

      <SummaryStrip summary={data?.summary} businessTime={businessTime} loading={isPending} />

      <FilterBar
        filters={filters}
        onChange={change}
        // Reset is the default view: the current month on whole days and
        // nothing narrowed. Keeping the date range made the button look dead
        // whenever the dates were the only thing changed.
        onReset={() => onFilters(withFilterChange(filters, {
          ...defaultRange(businessTime, now),
          statuses: [], min: '', max: '', ptn: '', gateways: [], biller: '', beneficiary: '', serviceType: '', serviceLabel: '', providerTxid: '', page: 1,
        }))}
        busy={isFetching}
        showGatewayFilter={exposed.has(FIELD_GATEWAY)}
        showBeneficiaryFilter={beneficiaryMode && exposed.has(FIELD_BENEFICIARY)}
        showServiceTypeFilter={filterable.has(FIELD_SERVICE_TYPE)}
        serviceLabels={data?.filter_options?.service_labels ?? []}
        serviceTypes={data?.filter_options?.service_types ?? []}
        gateways={gateways.data ?? []}
        {...(settlementCutOff ? { settlementCutOff } : {})}
        {...(allBillersMode ? { billers: billers.data ?? [] } : {})}
      />

      {error ? (
        <EmptyState
          glyph="⚠"
          title={t('error.loadFailed')}
          body={
            error instanceof BffError && error.requestId
              ? `${error.message} — ${t('error.reference', { requestId: error.requestId })}`
              : error.message
          }
          actions={<Button onClick={() => void refetch()}>{t('error.retry')}</Button>}
        />
      ) : isPending || (isFetching && rows.length === 0) ? (
        /* `isFetching && !rows.length` matters as much as `isPending`.
           With `placeholderData: keepPreviousData`, changing a filter hands
           back the PREVIOUS result while the new query runs — and if that
           previous result was empty, the screen renders an empty state for a
           result that has not arrived. Resetting from a filter that matched
           nothing therefore showed "No transactions yet", claiming there is no
           data at all, for the 1-3s a real query takes. Showing the loading
           state instead is the difference between "wait" and a false
           statement. */
        <Skeleton variant="row" count={8} label={t('tx.loading')} />
      ) : rows.length === 0 ? (
        // Two different empty states. On day one the MVP has no history at
        // all, so "no data yet" is the common case — telling that user to
        // "try a different filter" would be actively misleading.
        narrowed ? (
          <EmptyState
            glyph="⌕"
            title={t('tx.emptyFilteredTitle')}
            body={t('tx.emptyFilteredBody')}
            actions={
              <Button variant="secondary" onClick={() => onFilters(withFilterChange(filters, {
                ...defaultRange(businessTime, now),
                statuses: [], min: '', max: '', ptn: '', gateways: [], biller: '', beneficiary: '', serviceType: '', serviceLabel: '', providerTxid: '', page: 1,
              }))}>
                {t('tx.reset')}
              </Button>
            }
          />
        ) : (
          <EmptyState glyph="◍" title={t('tx.emptyTitle')} body={t('tx.emptyBody')} />
        )
      ) : (
        <>
          <TransactionsTable
            rows={rows}
            exposed={exposed}
            {...(billerOfGateway ? { billerOfGateway } : {})}
            businessTime={businessTime}
            sortBy={filters.sortBy}
            sortDir={filters.sortDir}
            onSort={sort}
            onSelect={setSelected}
            selectedId={selected?.ptn}
            stale={isFetching}
            caption={t('tx.caption', {
              count: data?.pagination.total_records ?? rows.length,
              timezone: businessTime.timezone,
            })}
          />
          {data && (
            <Pagination
              page={data.pagination.page}
              totalPages={data.pagination.total_pages}
              totalRecords={data.pagination.total_records}
              onPage={(page) => onFilters({ ...filters, page })}
              labels={{
                region: t('tx.paginationRegion'),
                previous: t('tx.previous'),
                next: t('tx.next'),
                summary: t('tx.pageSummary', {
                  page: data.pagination.page,
                  pages: Math.max(data.pagination.total_pages, 1),
                  records: businessTime.number(data.pagination.total_records),
                }),
              }}
            />
          )}
        </>
      )}

      <DetailPanel
        transaction={selected}
        exposed={exposed}
        onClose={() => setSelected(null)}
        businessTime={businessTime}
        {...(settlementCutOff ? { settlementCutOff } : {})}
      />
    </>
  )
}
